import { useState, useEffect, useCallback } from "react";
import { api, useAuth } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

const AMOUNTS = [500, 1000, 2500, 5000];

export default function Wallet() {
  const { user, refreshUser } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [targetEmail, setTargetEmail] = useState("");

  const load = useCallback(() => api.get("/wallet").then((r) => setData(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const topup = async (amount) => {
    setBusy(true);
    try {
      const payload = targetEmail ? { amount, user_email: targetEmail } : { amount };
      const { data: res } = await api.post("/wallet/topup", payload);
      await load(); await refreshUser();
      toast.success(targetEmail ? `₹${amount} credited to ${res.user_email}` : `₹${amount} added to your balance`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Top-up failed");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="text-gray-400">Loading wallet…</div>;
  const debits = data.transactions.filter((t) => t.type === "debit");
  const spent = debits.reduce((s, t) => s + t.amount, 0);

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Wallet" subtitle="Pay only for what you use — one prepaid balance for every tool." icon="Wallet" color="#06B6D4" />

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Balance + add money */}
        <div className="lg:col-span-1 space-y-5">
          <div className="rounded-[24px] bg-[#0A0A0A] text-white p-7 relative overflow-hidden">
            <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full grad-lime-cyan opacity-25 blur-3xl" />
            <span className="text-sm text-gray-400 relative">Available Balance</span>
            <div className="font-head font-extrabold text-5xl mt-2 relative" data-testid="wallet-balance">₹{data.balance.toLocaleString("en-IN")}</div>
            <div className="mt-4 flex items-center gap-2 text-xs text-gray-400 relative"><Icon name="TrendingDown" size={14} /> Spent this cycle: ₹{spent.toLocaleString("en-IN")}</div>
          </div>

          {isAdmin ? (
          <Card>
            <h3 className="font-head font-semibold text-lg mb-3">Add Credits <span className="text-[11px] font-semibold text-amber-600 bg-amber-50 rounded-full px-2 py-0.5 align-middle">ADMIN</span></h3>
            <div className="mb-3">
              <label className="text-xs font-semibold text-gray-500">Credit to (leave blank for your own balance)</label>
              <input value={targetEmail} onChange={(e) => setTargetEmail(e.target.value)} data-testid="wallet-target-email"
                placeholder="user@email.com" className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm outline-none focus:border-blue-400" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              {AMOUNTS.map((a) => (
                <button key={a} disabled={busy} onClick={() => topup(a)} data-testid={`wallet-add-${a}`}
                  className="py-3 rounded-xl border border-gray-200 font-semibold hover:border-blue-300 hover:bg-blue-50 transition-colors disabled:opacity-50">₹{a.toLocaleString("en-IN")}</button>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-3 flex items-center gap-1"><Icon name="ShieldCheck" size={12} /> Only administrators can add credits. Balances fund AI &amp; creative tools.</p>
          </Card>
          ) : (
          <Card>
            <h3 className="font-head font-semibold text-lg mb-2">Need more credits?</h3>
            <p className="text-sm text-gray-500">Your MFDx balance funds AI, content and creative tools. Credits are managed by MFDx — reach out to your administrator to top up.</p>
            <a href="mailto:support@mfdx.in?subject=MFDx%20credit%20top-up%20request" data-testid="wallet-request-credits" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[#0A0A0A] text-white text-sm font-semibold px-4 py-2.5 hover:brightness-110">
              <Icon name="Mail" size={15} /> Request credits
            </a>
          </Card>
          )}
        </div>

        {/* Usage + transactions */}
        <div className="lg:col-span-2 space-y-5">
          <Card>
            <h3 className="font-head font-semibold text-lg mb-4">Usage this cycle</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[["AI Tools", "#3B82F6", "Cpu"], ["Calling", "#8B5CF6", "PhoneCall"], ["Creative", "#F97316", "Palette"], ["Consultation", "#06B6D4", "UserCheck"]].map(([l, c, i], idx) => {
                const val = [340, 120, 180, 399][idx];
                return <div key={l} className="rounded-2xl p-4" style={{ background: `${c}12` }}>
                  <Icon name={i} size={18} style={{ color: c }} />
                  <div className="font-head font-bold text-xl mt-2">₹{val}</div>
                  <div className="text-xs text-gray-500">{l}</div>
                </div>;
              })}
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4"><h3 className="font-head font-semibold text-lg">Transaction History</h3>
              <Btn variant="outline" onClick={() => toast.info("Invoice PDF generated (demo)")}><Icon name="Download" size={14} /> Invoice</Btn></div>
            <div className="space-y-1">
              {data.transactions.map((t) => (
                <div key={t.id} className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0" data-testid="wallet-txn">
                  <div className={`w-9 h-9 rounded-xl grid place-items-center ${t.type === "credit" ? "bg-emerald-50" : "bg-red-50"}`}>
                    <Icon name={t.type === "credit" ? "ArrowDownLeft" : "ArrowUpRight"} size={16} className={t.type === "credit" ? "text-emerald-600" : "text-red-500"} />
                  </div>
                  <div className="min-w-0"><div className="text-sm font-medium truncate">{t.label}</div>
                    <div className="text-xs text-gray-400">{new Date(t.ts).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div></div>
                  <div className={`ml-auto font-semibold ${t.type === "credit" ? "text-emerald-600" : "text-gray-800"}`}>{t.type === "credit" ? "+" : "−"}₹{t.amount.toLocaleString("en-IN")}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <h3 className="font-head font-semibold text-lg mb-3">Pay-as-you-use pricing</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {data.pricing.map((p) => (
                <div key={p.key} className="rounded-2xl border border-gray-100 p-3">
                  <div className="text-sm font-semibold">{p.name}</div>
                  <div className="font-head font-bold text-lg text-blue-600">₹{p.price}<span className="text-xs text-gray-400 font-normal">/{p.unit}</span></div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
